import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../../firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import '../../styles/ResponderAvaliacao.css';

// Definições para as escalas padrão com emojis e rótulos.
const DEFAULT_SCALE_5_LEVELS = {
    min: 1, max: 5,
    levels: [
        { value: 1, label: 'Muito Abaixo', emoji: '😞' },
        { value: 2, label: 'Abaixo do Esperado', emoji: '😕' },
        { value: 3, label: 'Atende às Expectativas', emoji: '😐' },
        { value: 4, label: 'Acima do Esperado', emoji: '😊' },
        { value: 5, label: 'Excepcional', emoji: '🤩' }
    ]
};

const DEFAULT_SCALE_10_LEVELS = {
    min: 0, max: 10,
    levels: Array.from({ length: 11 }, (_, i) => ({ value: i }))
};


const ResponderAvaliacao = () => {
    const { responseId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [cycle, setCycle] = useState(null);
    const [evaluatedUser, setEvaluatedUser] = useState(null);
    const [answers, setAnswers] = useState({});
    const [scalesDetails, setScalesDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adminEmpresaId, setAdminEmpresaId] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const fetchCompanyAndUserData = async () => {
            const user = auth.currentUser;
            if (user) {
                const userDocSnap = await getDoc(doc(db, 'users', user.uid));
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setCurrentUserData({ uid: user.uid, ...userData });
                    if (userData.empresa_id) {
                        setAdminEmpresaId(userData.empresa_id);
                    } else { 
                        setError("Não foi possível identificar a empresa do usuário."); 
                    }
                }
            }
        };
        fetchCompanyAndUserData();
    }, []);

    useEffect(() => {
        const responsePath = location.state?.responsePath;
        if (!responsePath) {
            setError("Caminho da avaliação inválido. Por favor, acesse esta página através da lista de 'Minhas Tarefas'.");
            setLoading(false);
            return;
        }
        if (!adminEmpresaId || !currentUserData) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const responseDocRef = doc(db, responsePath);
                const responseSnap = await getDoc(responseDocRef);

                if (responseSnap.exists()) {
                    const responseData = responseSnap.data();
                    if(responseData.status === 'submitted') {
                        alert("Esta avaliação já foi respondida.");
                        navigate('/avaliacoes/minhas-tarefas');
                        return;
                    }

                    const cycleDocRef = responseDocRef.parent.parent;
                    const evaluatedUserDocRef = doc(db, 'users', responseData.evaluatedUserId);
                    const [cycleSnap, evaluatedUserSnap] = await Promise.all([ getDoc(cycleDocRef), getDoc(evaluatedUserDocRef) ]);

                    if (cycleSnap.exists() && evaluatedUserSnap.exists()) {
                        const cycleData = cycleSnap.data();
                        setCycle(cycleData);
                        setEvaluatedUser(evaluatedUserSnap.data());
                        setAnswers(responseData.answers || {});

                        const scaleIds = [...new Set(cycleData.form.map(q => q.type).filter(type => !type.startsWith('default')))];
                        const scalesMap = {
                            'default_0_5': DEFAULT_SCALE_5_LEVELS,
                            'default_0_10': DEFAULT_SCALE_10_LEVELS
                        };

                        if (scaleIds.length > 0) {
                            const scalesRef = collection(db, 'Empresas', adminEmpresaId, 'evaluationScales');
                            const q = query(scalesRef, where('__name__', 'in', scaleIds));
                            const scalesSnapshot = await getDocs(q);
                            scalesSnapshot.forEach(doc => { scalesMap[doc.id] = doc.data(); });
                        }
                        setScalesDetails(scalesMap);
                    } else { setError("Detalhes do ciclo ou do usuário não foram encontrados."); }
                } else { setError("Não foi possível carregar os dados desta avaliação."); }
            } catch(err) {
                console.error(err);
                setError("Ocorreu um erro ao carregar os dados.");
            }
            setLoading(false);
        };
        fetchData();
    }, [location.state, navigate, adminEmpresaId, currentUserData]);
    
    const handleAnswerChange = (questionId, field, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], [field]: value }
        }));

        if (validationErrors[questionId]) {
            setValidationErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[questionId];
                return newErrors;
            });
        }
    };

const visibleQuestions = cycle?.form.filter(question => {
    // Se a pergunta não tem restrição de departamento, exibe sempre
    if (!question.departments || question.departments.length === 0) {
        return true;
    }
    
    // CORREÇÃO: Verifica se o departamento do AVALIADO (evaluatedUser) corresponde ao filtro da pergunta.
    // Assim, o gestor verá as perguntas pertinentes à área do funcionário, não à sua própria.
    return question.departments.some(dept => dept.value === evaluatedUser?.departamento);
}) || [];

    const handleSubmit = async () => {
        const errors = {};
        for (const question of visibleQuestions) {
            const answer = answers[question.questionId];
            if (answer?.score === undefined || answer.score === null) {
                errors[question.questionId] = 'Por favor, selecione uma nota para continuar.';
            } else if (question.isJustificationMandatory && (!answer?.justification || answer.justification.trim() === '')) {
                errors[question.questionId] = 'A justificativa é obrigatória para esta pergunta.';
            }
        }

        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            return; 
        }

        if (!window.confirm("Tem certeza que deseja enviar suas respostas? Você não poderá editá-las depois.")) return;

        setLoading(true);
        const responsePath = location.state?.responsePath;
        const responseDocRef = doc(db, responsePath);
        try {
            await updateDoc(responseDocRef, { status: 'submitted', answers: answers, submittedAt: new Date() });
            alert("Avaliação enviada com sucesso!");
            navigate('/avaliacoes/minhas-tarefas');
        } catch (error) {
            console.error("Erro ao enviar avaliação: ", error);
            alert("Ocorreu um erro ao enviar sua avaliação.");
            setLoading(false);
        }
    };

    if (loading) return <div className="response-container"><p>Carregando avaliação...</p></div>;
    if (error) return <div className="response-container"><p style={{color: 'red'}}>{error}</p></div>;

    return (
        <div className="response-container">
            <div className="response-header">
                <h2>{cycle?.name}</h2>
                <p>Você está avaliando: <strong>{evaluatedUser?.name}</strong></p>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                {visibleQuestions.map((question, index) => {
                    const currentAnswer = answers[question.questionId] || {};
                    const scale = scalesDetails[question.type];
                    
                    const hasCustomLevels = scale?.levels?.some(l => l.emoji || l.label || l.imageUrl);
                    const scaleValues = scale?.levels || [];

                    return (
                        <div key={question.questionId} className={`question-block ${validationErrors[question.questionId] ? 'has-error' : ''}`}>
                            <label className="question-text">{index + 1}. {question.text}</label>
                            
                            {scale && (
                                <div className={`scale-container ${hasCustomLevels ? 'emoji-scale' : 'number-scale'}`}>
                                    {scaleValues.map(level => (
                                        <label key={level.value} className="scale-option">
                                            <input 
                                                type="radio" 
                                                name={`question-score-${question.questionId}`} 
                                                value={level.value} 
                                                checked={String(currentAnswer.score) === String(level.value)} 
                                                onChange={(e) => handleAnswerChange(question.questionId, 'score', e.target.value)} 
                                            />
                                            
                                            <div className="scale-content-wrapper">
                                                {level.imageUrl ? (
                                                    <div className="scale-image-container">
                                                        <img src={level.imageUrl} alt={level.label} className="scale-image"/>
                                                    </div>
                                                ) : level.emoji ? (
                                                    <span className="scale-emoji">{level.emoji}</span>
                                                ) : (
                                                    <span className="scale-number">{level.value}</span>
                                                )}
                                                <span className="scale-label">{level.label}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}

                            <div className="justification-container">
                                <label htmlFor={`justification-text-${question.questionId}`} className="justification-label">
                                    Justificativa {question.isJustificationMandatory ? <span className="required-tag">(obrigatório)</span> : '(opcional)'}
                                </label>
                                <textarea 
                                    id={`justification-text-${question.questionId}`}
                                    onChange={(e) => handleAnswerChange(question.questionId, 'justification', e.target.value)} 
                                    value={currentAnswer.justification || ''} 
                                    placeholder="Escreva seus comentários para contextualizar a nota..." 
                                    rows="4" 
                                />
                            </div>
                            
                            {/* ALTERAÇÃO AQUI: Usando a classe 'required-tag' para a mensagem de erro */}
                            {validationErrors[question.questionId] && (
                                <p className="required-tag-erro" style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                                    {validationErrors[question.questionId]}
                                </p>
                            )}
                        </div>
                    );
                })}
                <div className="response-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Enviando...' : 'Enviar Avaliação'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ResponderAvaliacao;